/* Auriani PDP v8a (v7.0 step 2C, 03.11.2026): the onecklace-style product page, photographs only.
   Built from PDP v7p (work/launch-v6/theme-new/assets/personalized-pdp.js) with the drawing removed:
   no live preview, mockup library or worker is loaded. Kept exactly as in v7p: the options from the
   product's variants, the fields from beleco.personalization (maxLength, minLength, fontMax, scripts),
   the 30 fonts, the default font of the product photo, the cart line properties (supplier keys,
   _jwl_font, _auriani_note), AurianiCart.add / replace, the ?edit=<line key> reload, p_* parameters and
   the names + metal remembered across products (localStorage auriani:last-choice:v1).
   New: the font sample beside each name field, metal tiles with prices, birthstone image dropdown,
   chain-length tiles with a size guide drawer, gallery metal swap and zoom, mobile sticky bar.
   v8b (v7.1, 03.11.2026), display only, the cart line is unchanged:
   M1 finish names ("14k Gold Plated" where the piece's material says 14k plating), M2 metal tiles as a
   radiogroup (arrow keys, roving focus, check mark), M3 metal guide, M4 "Size & materials" facts table,
   P1 delivery estimate under Add to bag (days from the theme settings), P2 length fit hints,
   P4 lazy font faces (selected font first), G1 only the chosen metal in the gallery.
   v7.1 (owner rule, 03.11): every metal-coloured drawing follows the chosen finish ([data-au-finish] on the page:
   chain-style tiles and the gold length chart are recoloured in CSS, the length drawings use currentColor).
   v7.4 (owner, 03.11): one sticky purchase bar on desktop and mobile (photo of the chosen finish, title, choices,
   price, "Customize" until the personalization is complete, then "Add to bag" through the main button). */
(() => {
  const root = document.querySelector('[data-ppdp-root]');
  if (!root) return;
  const read = selector => JSON.parse(document.querySelector(selector).textContent);
  const product = read('[data-ppdp-product]');
  const config = read('[data-ppdp-config]');
  const page = root.closest('[data-au-pdp]') || document;
  const fields = Array.isArray(config.fields) ? config.fields : [];
  const ui = config.ui || {};
  const purchasable = config.purchasable !== false;
  const sid = config.section || '';

  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  const money = cents => {
    const [whole, frac] = (cents / 100).toFixed(2).split('.');
    const commas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (config.money || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) =>
      k === 'amount_no_decimals' ? commas : k === 'amount_with_comma_separator' ? `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${frac}` : `${commas}.${frac}`);
  };
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobile = () => matchMedia('(max-width: 768px)').matches;

  // ---- options from the product's variants (as v7p) -----------------------------------
  const optionNames = product.options.map(o => o.name || o);
  const iMetal = optionNames.findIndex(n => /material|metal|finish/i.test(n));
  const chainOptions = optionNames.map((n, i) => i).filter(i => i !== iMetal);
  const valuesOf = i => [...new Set(product.variants.map(v => v.options[i]))];
  const chainFor = v => ['Cable', 'Box', 'Figaro', 'Paperclip'].find(c => new RegExp(c, 'i').test(v || '')) || 'Cable';
  const finishOf = v => /rose/i.test(v || '') ? 'rose' : /gold/i.test(v || '') ? 'gold' : 'silver';
  const lengthNumber = v => parseFloat(v) || 0;
  const DELIVERS_TO = [['US', 'United States'], ['CA', 'Canada']];
  const singleDefault = product.variants.length === 1 && /^default title$/i.test(product.variants[0].title || '');
  // M1: display names only. The variant option values, the URL and the cart line keep the variant's own values.
  const descItems = (() => { const d = document.createElement('template'); d.innerHTML = String(product.description || '').split('<!--COLLECTIVE_NOTE_END-->').pop(); return [...d.content.querySelectorAll('li')].map(li => li.textContent.replace(/\s+/g, ' ').trim()); })();
  const materialText = (descItems.find(s => /^materials?\s*:/i.test(s)) || '').replace(/^materials?\s*:\s*/i, '');
  const plating14k = /14k/i.test(materialText) && /plating|plated/i.test(materialText);
  const roseIn14k = plating14k && /rose/i.test(materialText);
  const nickelFree = /hypoallergenic/i.test(materialText) && /nickel[- ]free/i.test(materialText);
  const finishLabel = v => {
    const s = String(v || '');
    if (/925|sterling/i.test(s)) return '925 Sterling Silver';
    if (/rose/i.test(s)) return roseIn14k ? '14k Rose Gold Plated' : /plated/i.test(s) ? 'Rose Gold Plated' : s;
    if (/gold/i.test(s)) return plating14k ? '14k Gold Plated' : s;
    return s;
  };
  const finishLine = v => {
    const s = String(v || '');
    if (/925|sterling/i.test(s)) return '925 sterling silver: 92.5% silver, the standard for fine silver jewelry.';
    if (/rose/i.test(s)) return roseIn14k ? 'A 14k rose gold plating, for a soft pink-gold colour.' : 'A rose gold colour finish.';
    if (/gold/i.test(s)) return plating14k ? 'A 14k gold plating, for a warm yellow-gold colour.' : 'A gold colour finish.';
    if (/plated/i.test(s)) return 'A bright silver-colour plated finish.';
    return 'A silver-colour finish.';
  };

  // ---- personalization fields (as v7p) --------------------------------------------------
  const textFields = fields.filter(f => f.type === 'text');
  const FONT_FILES = Array.from({length: 30}, (_, i) => i + 1);
  const FILES_FONTS = [16, 18, 24, 29];
  const assets = config.assets || {};
  const fontFile = n => FILES_FONTS.includes(Number(n)) ? `${assets.fontFiles || ''}auriani-font-${n}.otf` : `asset:auriani-font-${n}.ttf`;
  fields.forEach(f => {
    if (f.type !== 'font' || f.fonts) return;
    if (f.set === 'numbered') f.fonts = Array.from({length: f.count || 1}, (_, i) => ({n: i + 1, id: `n${i + 1}`}));
    if (f.set !== 'beleco') return;
    f.fonts = Array.from({length: f.count || 30}, (_, i) => {
      const n = i + 1, file = fontFile(n);
      return FONT_FILES.includes(n) ? {n, id: `b${n}`, tile: file, face: file, sample: 'Abc'} : {n, id: `b${n}`};
    });
  });
  const fontField = fields.find(f => f.type === 'font' && Array.isArray(f.fonts) && f.fonts.length);
  const assetUrl = u => typeof u === 'string' && u.startsWith('asset:') ? (assets.themeAssets || '') + u.slice(6) : u;
  fontField?.fonts.forEach(f => { f.tile = assetUrl(f.tile); f.face = assetUrl(f.face); });
  const selectFields = fields.filter(f => f.type === 'select' && Array.isArray(f.options));
  const pattern = f => f.script === 'latin' ? /^[A-Za-z][A-Za-z '.-]*$/ : f.script === 'date' ? /^[0-9IVXLCDM][0-9IVXLCDM .\/-]*$/i : /^[\p{L}][\p{L}\p{M} '.-]*$/u;
  const limitFor = f => {
    const n = fontField ? state.values[fontField.key] : null;
    const byFont = n && f.fontMax ? f.fontMax[n] : null;
    return byFont && (!f.maxLength || byFont < f.maxLength) ? byFont : f.maxLength;
  };
  const textError = (f, value) => {
    const v = value.trim();
    if (!v) return f.required ? 'Please enter this ' + (f.label || f.key).toLowerCase() + '.' : null;
    if (f.minLength && v.length < f.minLength) return `Use at least ${f.minLength} characters.`;
    const max = limitFor(f);
    if (max && v.length > max) return max < f.maxLength ? `Font ${state.values[fontField.key]} fits up to ${max} characters on this piece. Shorten the text or choose another font.` : `Use ${max} characters or fewer.`;
    if (!pattern(f).test(v)) return f.script === 'latin' ? 'Use English letters, spaces, apostrophes, hyphens or periods.' : f.script === 'date' ? 'Use digits or Roman numerals, for example 1998 or 09.07.2020.' : 'Use letters, spaces, apostrophes, hyphens or periods.';
    return null;
  };

  // ---- state, restored from the address (as v7p) -------------------------------------------
  const params = new URLSearchParams(location.search);
  let editKey = params.get('edit');
  if (editKey && !params.has('edit_loaded')) {
    fetch('/cart.js', {credentials: 'same-origin'}).then(r => r.json()).then(cart => {
      const line = cart.items.find(i => i.key === editKey && i.product_id === product.id);
      const url = new URL(location.href);
      if (line) {
        url.searchParams.set('variant', String(line.variant_id));
        fields.forEach(f => { const v = line.properties?.[f.key]; v ? url.searchParams.set(`p_${f.key}`, v) : url.searchParams.delete(`p_${f.key}`); });
        url.searchParams.set('edit_loaded', '1');
      } else url.searchParams.delete('edit');
      location.replace(url);
    }).catch(() => {});
  }
  const start = product.variants.find(v => String(v.id) === (params.get('variant') || String(config.variant))) || product.variants[0];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const photoDefaults = config.photoDefaults?.[product.id] || {};
  if (config.next && typeof config.next === 'object') ui.render_v8r = config.next;
  const recipe = ui.render_v8r || ui.render_next || ui.render;
  const photoFont = photoDefaults.font ?? recipe?.font;
  const preset = f => f.type === 'font' ? String(f.fonts?.some(x => String(x.n) === String(photoFont) && x.face) ? photoFont : f.fonts?.[0]?.n ?? '')
    : f.type === 'select' ? (f.options.includes(MONTHS[new Date().getMonth()]) ? MONTHS[new Date().getMonth()] : f.options[0] || '')
    : '';
  const state = {
    options: [...start.options],
    values: Object.fromEntries(fields.map(f => [f.key, params.get(`p_${f.key}`) || preset(f)]))
  };
  const LAST = 'auriani:last-choice:v1';
  const recall = () => { try { return JSON.parse(localStorage.getItem(LAST) || 'null') || {}; } catch (_) { return {}; } };
  const nameFields = textFields.filter(f => /name/i.test(f.key));
  if (!editKey) {
    const last = recall();
    nameFields.filter(f => !params.has(`p_${f.key}`)).forEach(f => {
      const v = last.names?.[f.key];
      if (v && !textError(f, v)) state.values[f.key] = v;
    });
    if (iMetal >= 0 && !params.has('variant') && last.metal) {
      const options = state.options.map((o, i) => i === iMetal ? last.metal : o);
      if (product.variants.some(x => x.available && x.options.every((o, i) => o === options[i]))) state.options = options;
    }
    // Chain length starts at 18" where the piece offers it, as the most worn length (onecklace default).
    const iLen = optionNames.findIndex(n => /length/i.test(n));
    if (iLen >= 0 && !params.has('variant')) {
      const v18 = valuesOf(iLen).find(v => /^18\s*["”]/.test(v) || lengthNumber(v) === 18);
      const options = state.options.map((o, i) => i === iLen && v18 ? v18 : o);
      if (v18 && product.variants.some(x => x.available && x.options.every((o, i) => o === options[i]))) state.options = options;
    }
  }
  const remember = () => {
    const last = recall(), names = {...last.names};
    nameFields.forEach(f => { const v = state.values[f.key].trim(); if (v && !textError(f, v)) names[f.key] = v; });
    try { localStorage.setItem(LAST, JSON.stringify({names, metal: iMetal >= 0 ? state.options[iMetal] : last.metal, t: Date.now()})); } catch (_) {}
  };
  const variant = () => product.variants.find(v => v.options.every((o, i) => o === state.options[i]));
  const chosenFont = () => fontField?.fonts.find(f => String(f.n) === String(state.values[fontField.key]));
  // Pieces made in one lettering style (no Font field) show their text in that style.
  const fixedStyle = !fontField ? (recipe?.style || ui.preview?.style || ui.photo?.style || null) : null;
  const fixedFace = fixedStyle ? assetUrl(fontFile(fixedStyle)) : null;
  const DEFAULT_EXAMPLES = ['Emily', 'Noa', 'Liam', 'Ava'];
  const colorField = textFields.find(f => /colou?r/i.test(f.key));
  const examples = recipe?.example?.length === textFields.length ? recipe.example : (photoDefaults.example?.length === textFields.length ? photoDefaults.example : textFields.map((f, i) => f === colorField ? 'Red' : f.script === 'date' ? '1998' : f.maxLength === 1 ? 'A' : DEFAULT_EXAMPLES[i % 4]));

  // ---- supplier charts among the product photos ----------------------------------------------
  const photos = product.media.filter(m => m.media_type === 'image').map(m => ({id: m.id, src: m.preview_image?.src || m.src, alt: m.alt || product.title}));
  const sized = (src, w) => `${src}${src.includes('?') ? '&' : '?'}width=${w}`;
  const file = p => p.src.split('?')[0];
  const fontCharts = photos.filter(p => /\/(FONTS-|SAMPLE[-_ ])[^/]*$/i.test(file(p)) || /font chart · AURIANI|chart of .*(fonts|lettering)/i.test(p.alt));
  const CHAIN_CHARTS = [
    {match: /CHAIN_TYPES/i, crops: {Cable: [.275, .249, .45, .043], Box: [.275, .368, .45, .037], Figaro: [.275, .478, .45, .057], Paperclip: [.275, .612, .45, .043]}},
    {match: /CABLE-BOX-FIGRO-PAPER/i, crops: {Cable: [.2, .19, .6, .044], Box: [.2, .376, .6, .039], Figaro: [.2, .576, .6, .069], Paperclip: [.2, .798, .6, .052]}},
    {match: /CABLE-BOX-WHEAT/i, crops: {Cable: [.2, .365, .6, .044], Box: [.2, .523, .6, .039], Wheat: [.2, .663, .6, .041]}},
    {match: /CABLE-BOX/i, crops: {Cable: [.2, .421, .6, .049], Box: [.2, .649, .6, .031]}}
  ];
  const chart = CHAIN_CHARTS.map(c => ({...c, photo: photos.find(p => c.match.test(p.src))})).find(c => c.photo);
  const lengthGuide = ui.lengthGuide || photos.find(p => /CHAIN_LENGTH|\/cl_[^/]*$/i.test(file(p)))?.src;
  // Birthstones are cut from the supplier's own chart (centres as fractions of the chart).
  const STONE_CHARTS = [
    {match: /\/(ILLUSTRATION|BIRTHSTONE)/, size: .07, centres: {January: [.306, .402], February: [.306, .515], March: [.306, .643], April: [.306, .778], May: [.501, .402], June: [.501, .515], July: [.501, .643], August: [.501, .778], September: [.693, .402], October: [.693, .515], November: [.693, .643], December: [.693, .778]}},
    // the shared Beleco chart 36763082-afe4-… (measured on its 800 px file, 03.11)
    {match: /36763082-afe4-48bc-886c-bba8141af1ed/, size: .068, centres: {January: [.138, .309], February: [.138, .445], March: [.138, .591], April: [.138, .7375], May: [.5, .309], June: [.5, .445], July: [.5, .591], August: [.5, .7375], September: [.86, .309], October: [.86, .445], November: [.86, .591], December: [.86, .7375]}}
  ];
  const stoneChart = STONE_CHARTS.map(c => ({...c, photo: photos.find(p => c.match.test(file(p)))})).find(c => c.photo);
  const STONES = {January: '#8a0f33', February: '#7d68a8', March: '#2fc9d6', April: '#e9f3f7', May: '#00866a', June: '#c9c2e2', July: '#c51a3c', August: '#a6d23f', September: '#1f6fe0', October: '#eba8c2', November: '#e6d84f', December: '#0b8ab3'};
  const crop = (src, [x, y, w, h], cls) => `<span class="${cls}" aria-hidden="true" style="aspect-ratio:${(w / h).toFixed(3)};background-image:url('${sized(src, 1000)}');background-size:${(100 / w).toFixed(2)}% ${(100 / h).toFixed(2)}%;background-position:${(x / (1 - w) * 100).toFixed(2)}% ${(y / (1 - h) * 100).toFixed(2)}%"></span>`;
  const stoneImage = month => {
    const c = stoneChart?.centres[month];
    if (c) { const s = stoneChart.size; return crop(stoneChart.photo.src, [c[0] - s / 2, c[1] - s / 2, s, s], 'au-stone'); }
    return STONES[month] ? `<span class="au-stone au-stone--dot" aria-hidden="true" style="--au-stone:${STONES[month]}"></span>` : '';
  };
  const chainImage = v => {
    const name = ['Cable', 'Box', 'Figaro', 'Paperclip', 'Wheat'].find(c => new RegExp(c, 'i').test(v || ''));
    const box = name && chart?.crops[name];
    return box ? crop(chart.photo.src, box, 'au-tile__chain') : '';
  };
  // Length tiles: a small drawn neckline with the chain where that length falls (from v7p).
  const LENGTH_DEPTH = {14: 33, 16: 43, 18: 53, 20: 65, 22: 78, 24: 92};
  const lengthArt = v => {
    const inches = Math.min(Math.max(Math.round(lengthNumber(v) / 2) * 2, 14), 24);
    const d = LENGTH_DEPTH[inches];
    const chain = `M47 27 C ${49 + (d - 27) * 0.05} ${d * 0.98}, ${71 - (d - 27) * 0.05} ${d * 0.98}, 73 27`;
    return `<span class="au-tile__art" aria-hidden="true"><svg viewBox="0 0 120 120" width="46" height="46">
      <path d="M44 4v18c0 6-4 9-10 11C22 37 12 40 6 46" fill="none" stroke="#8f877e" stroke-width="4" stroke-linecap="round"/>
      <path d="M76 4v18c0 6 4 9 10 11 12 4 22 7 28 13" fill="none" stroke="#8f877e" stroke-width="4" stroke-linecap="round"/>
      <path d="${chain}" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round"/>
      <rect x="54.5" y="${(d * 0.98 * 0.75 + 27 * 0.25 - 1).toFixed(1)}" width="11" height="8" rx="2.5" fill="currentColor"/></svg></span>`;
  };
  // P2: where the chain sits, standard fit wording (necklaces only).
  const LENGTH_FIT = {14: 'Child / choker', 16: 'Collarbone', 18: 'Just below the collarbone', 20: 'Above the neckline', 22: 'At the neckline', 24: 'Below the neckline'};
  const fitHint = v => { const n = lengthNumber(v); return n >= 14 && n <= 24 && Math.abs(n - Math.round(n / 2) * 2) < .01 ? LENGTH_FIT[Math.round(n / 2) * 2] || '' : ''; };

  // ---- markup ----------------------------------------------------------------------------------
  const check = '<span class="au-tile__check" aria-hidden="true"><svg viewBox="0 0 12 12" width="10" height="10"><path d="M2.5 6.2l2.4 2.3 4.6-5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg></span>';
  const chevron = '<svg class="au-dd__chev" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
  const blocks = [];
  if (iMetal >= 0 && !singleDefault) {
    const metals = valuesOf(iMetal).sort((a, b) => /925/.test(a) - /925/.test(b));
    blocks.push(`<div class="au-opt au-opt--metal">
      <div class="au-opt__rule"><p class="au-opt__title" id="AuPdpMetalT">${esc(optionNames[iMetal] === 'Finish' ? 'Finish' : 'Metal')}: <b data-metal-name></b></p><button type="button" class="au-opt__help" data-open-dialog="AuPdpMaterial-${esc(sid)}" aria-label="Compare the finishes" aria-haspopup="dialog">?</button></div>
      <div class="au-metals" role="radiogroup" aria-labelledby="AuPdpMetalT">${metals.map(v => `<button type="button" class="au-metal" role="radio" aria-checked="false" tabindex="-1" data-option="${iMetal}" data-value="${esc(v)}"><span class="au-metal__dot au-metal__dot--${finishOf(v)}" aria-hidden="true">${/925/.test(v) ? '925' : ''}</span><span class="au-metal__name">${esc(finishLabel(v))}</span><span class="au-metal__price" data-metal-price="${esc(v)}"></span>${check}</button>`).join('')}</div>
    </div>`);
  }
  textFields.forEach((f, i) => blocks.push(`<div class="au-opt au-opt--text" data-field="${esc(f.key)}">
      <div class="au-opt__head"><label class="au-opt__label" for="AuPdpF${i}">${esc(textFields.length === 1 && /^name$/i.test(f.key) ? 'Write name / word' : f.label || f.key)}${f.required === false ? ' <span class="au-opt__optional">(optional)</span>' : ''}</label><span class="au-opt__count" data-left="${esc(f.key)}" aria-live="polite"></span></div>
      <div class="au-name">
        <div class="au-name__field"><input class="au-input au-name__input" id="AuPdpF${i}" data-text="${esc(f.key)}" ${f.maxLength ? `maxlength="${f.maxLength + 4}"` : ''} autocomplete="off" autocapitalize="words" spellcheck="false" aria-describedby="AuPdpF${i}-err" ${f.required ? 'required aria-required="true"' : ''}>
          <button type="button" class="au-name__clear" data-clear="${esc(f.key)}" aria-label="Clear ${esc(f.label || f.key)}" hidden><span aria-hidden="true">×</span></button></div>
        <div class="au-name__sample" aria-hidden="true"><span class="au-name__text" data-sample="${esc(f.key)}"></span></div>
      </div>
      <p class="ppdp__error au-error" id="AuPdpF${i}-err" data-error="${esc(f.key)}" role="alert"></p>
    </div>`));
  const FONT_FIRST = 12;
  if (fontField) blocks.push(`<div class="au-opt au-opt--font" data-step="font">
      <div class="au-opt__head"><p class="au-opt__label">${esc(fontField.label || 'Font')}: <b data-font-name></b></p>${fontCharts.length ? '<button type="button" class="au-opt__link" data-font-chart>Font chart</button>' : ''}</div>
      <div class="au-fonts" role="radiogroup" aria-label="${esc(fontField.label || 'Font')}">${fontField.fonts.map((f, i) =>
        `<button type="button" class="au-tile au-font" role="radio" aria-checked="false" data-font="${f.n}"${i >= FONT_FIRST ? ' data-font-more hidden' : ''}>${f.tile ? `<span class="au-font__sample" data-font-sample data-face="${f.id}" style="font-family:'ppdp-tile-${f.id}',serif">${esc(f.sample || 'Abc')}</span>` : `<span class="au-font__sample au-font__sample--num">${f.n}</span>`}<span class="au-tile__label">Font ${f.n}</span>${check}</button>`).join('')}</div>
      ${fontField.fonts.length > FONT_FIRST ? `<button type="button" class="au-opt__more" data-font-toggle aria-expanded="false">Show all ${fontField.fonts.length} fonts</button>` : ''}
      <p class="ppdp__error au-error" data-error="${esc(fontField.key)}" role="alert"></p>
    </div>`);
  selectFields.forEach((f, i) => {
    const stones = f.options.some(o => STONES[o]);
    if (stones) blocks.push(`<div class="au-opt au-opt--stone" data-step="select${i}">
      <div class="au-opt__head"><p class="au-opt__label" id="AuPdpS${i}">${esc(/month/i.test(f.label || f.key) ? 'Choose birthstone' : f.label || f.key)}</p>${stoneChart ? `<button type="button" class="au-opt__link" data-stone-guide>Birthstone guide</button>` : ''}</div>
      <div class="au-dd" data-dd>
        <button type="button" class="au-dd__toggle" data-dd-toggle aria-haspopup="listbox" aria-expanded="false" aria-labelledby="AuPdpS${i} AuPdpS${i}v"><span class="au-dd__img" data-dd-img></span><span id="AuPdpS${i}v" data-dd-label>Select birthstone</span>${chevron}</button>
        <ul class="au-dd__list" role="listbox" aria-labelledby="AuPdpS${i}" data-dd-list hidden>${f.options.map(o => `<li role="none"><button type="button" class="au-dd__opt" role="option" aria-selected="false" data-select="${esc(f.key)}" data-value="${esc(o)}">${stoneImage(o)}<span>${esc(o)}</span></button></li>`).join('')}</ul>
      </div>
      <p class="ppdp__error au-error" data-error="${esc(f.key)}" role="alert"></p>
    </div>`);
    else blocks.push(`<div class="au-opt" data-step="select${i}">
      <div class="au-opt__head"><p class="au-opt__label">${esc(f.label || f.key)}: <b data-summary="select${i}"></b></p></div>
      <div class="au-tiles" role="radiogroup" aria-label="${esc(f.label || f.key)}">${f.options.map(o => `<button type="button" class="au-tile" role="radio" aria-checked="false" data-select="${esc(f.key)}" data-value="${esc(o)}"><span class="au-tile__label">${esc(o)}</span>${check}</button>`).join('')}</div>
      <p class="ppdp__error au-error" data-error="${esc(f.key)}" role="alert"></p>
    </div>`);
  });
  if (!singleDefault) chainOptions.forEach(i => {
    const values = valuesOf(i);
    const isLength = /length/i.test(optionNames[i]);
    const sorted = isLength ? values.sort((a, b) => lengthNumber(a) - lengthNumber(b)) : values;
    const guide = isLength && lengthGuide ? '<button type="button" class="au-opt__link" data-guide>Size Guide</button>' : '';
    const title = isLength ? (/chain/i.test(optionNames[i]) ? 'Choose chain length' : `Choose ${optionNames[i].toLowerCase()}`) : /chain/i.test(optionNames[i]) ? 'Choose chain style' : optionNames[i];
    blocks.push(`<div class="au-opt${isLength ? ' au-opt--length' : ' au-opt--chain'}">
      <div class="au-opt__head"><p class="au-opt__label">${esc(title)}: <b data-summary="option${i}"></b></p>${guide}</div>
      <div class="au-tiles${isLength ? ' au-tiles--length' : ' au-tiles--chain'}" role="radiogroup" aria-label="${esc(optionNames[i])}">${sorted.map(v => {
        const art = isLength ? (lengthNumber(v) >= 14 && /neck|chain/i.test(optionNames[i] + ' ' + (product.type || '') + ' ' + product.title) ? lengthArt(v) : '') : chainImage(v);
        const hint = isLength && art ? fitHint(v) : '';
        return `<button type="button" class="au-tile${art ? ' au-tile--art' : ''}" role="radio" aria-checked="false" data-option="${i}" data-value="${esc(v)}">${art}<span class="au-tile__label">${esc(v)}</span>${hint ? `<span class="au-tile__hint">${esc(hint)}</span>` : ''}<span class="au-tile__delta" data-delta="${i}|${esc(v)}"></span>${check}</button>`;
      }).join('')}</div>
    </div>`);
  });
  const addMarkup = purchasable ? `<div class="au-opt au-opt--add">
      <button type="button" class="au-btn au-btn--lg au-btn--block au-pdp-add" data-add><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5.2 8h13.6l-.9 12.5H6.1L5.2 8z"/><path d="M9 10.5V7a3 3 0 0 1 6 0v3.5"/></svg><span data-add-label></span></button>
      <p class="au-error au-pdp-add__error" data-add-error role="alert"></p>
      <p class="au-pdp-eta" data-au-eta-line hidden><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.6"/><circle cx="17" cy="17.5" r="1.6"/></svg><span data-au-eta-text></span></p>
      <form class="au-pdp-ship" data-ship-country method="post" action="/localization" hidden>
        <input type="hidden" name="form_type" value="localization"><input type="hidden" name="utf8" value="✓">
        <input type="hidden" name="_method" value="put">
        <input type="hidden" name="return_to" value="${esc(location.pathname + location.search)}">
        <p>We don't deliver to your country yet. You can still design your piece and have it sent to:</p>
        <div class="au-inline-form"><label class="au-visually-hidden" for="AuPdpShip">Delivery country</label>
          <select class="au-select" id="AuPdpShip" name="country_code">${DELIVERS_TO.map(([code, name]) => `<option value="${code}">${name}</option>`).join('')}</select>
          <button type="submit" class="au-btn au-btn--secondary">Update</button></div>
      </form>
    </div>` : '';
  root.innerHTML = blocks.join('') + addMarkup;
  root.removeAttribute('aria-busy');
  const $ = s => root.querySelector(s);
  const $$ = s => [...root.querySelectorAll(s)];
  const P = s => page.querySelector(s);
  const PP = s => [...page.querySelectorAll(s)];

  // ---- fonts: every tile and the sample letter the customer's text in its own face -------------
  function fitSample(n, min = 9) {
    n.style.fontSize = '';
    for (let pass = 0; pass < 2 && n.clientWidth && n.scrollWidth > n.clientWidth; pass++)
      n.style.fontSize = `${Math.max(min, Math.floor(parseFloat(getComputedStyle(n).fontSize) * n.clientWidth / n.scrollWidth * 0.95))}px`;
  }
  const format = u => /\.woff2(\?|$)/i.test(u) ? " format('woff2')" : '';
  // P4: each face loads only when it is needed: the selected font (and a fixed style) at once, the tiles in
  // view when the picker nears the screen, the rest on "Show all". Nothing is drawn in a face until it has
  // loaded, so the sample never flashes in the wrong font.
  const faceUrl = new Map([...(fontField?.fonts.filter(f => f.tile) || []).map(f => [f.id, f.face || f.tile]), ...(fixedFace ? [['fixed', fixedFace]] : [])]);
  const faceState = new Map();
  const refit = () => { $$('[data-font-sample]').forEach(n => fitSample(n)); $$('[data-sample]').forEach(n => fitSample(n, 12)); };
  function loadFace(id) {
    if (!faceUrl.has(id)) return Promise.resolve(false);
    if (faceState.has(id)) return faceState.get(id);
    const u = faceUrl.get(id);
    let p;
    if (window.FontFace && document.fonts?.add) {
      const ff = new FontFace(`ppdp-tile-${id}`, `url(${JSON.stringify(u)})${format(u)}`, {display: 'block'});
      document.fonts.add(ff);
      p = Promise.race([ff.load().then(() => true, () => false), new Promise(r => setTimeout(() => r(false), 4000))]);
    } else {
      document.head.insertAdjacentHTML('beforeend', `<style>@font-face{font-family:'ppdp-tile-${id}';src:url(${JSON.stringify(u)})${format(u)};font-display:block}</style>`);
      p = Promise.resolve(true);
    }
    p = p.then(ok => { $$(`[data-face="${id}"]`).forEach(n => { n.classList.add('is-ready'); fitSample(n); }); if (ok) refit(); return ok; });
    faceState.set(id, p);
    return p;
  }
  const faceOf = node => node.getAttribute('data-face');
  const loadVisibleTiles = () => $$('[data-font] [data-face]').filter(n => !n.closest('[hidden]')).forEach(n => loadFace(faceOf(n)));
  if (faceUrl.size && 'ResizeObserver' in window) new ResizeObserver(refit).observe(root);
  const sampleFamily = () => { const f = chosenFont(); return f?.tile ? `'ppdp-tile-${f.id}', serif` : fixedFace ? "'ppdp-tile-fixed', serif" : 'var(--au-font-display)'; };
  const sampleFace = () => { const f = chosenFont(); return f?.tile ? f.id : fixedFace ? 'fixed' : ''; };
  // the selected face starts loading before the first paint of the sample
  if (sampleFace()) loadFace(sampleFace());
  const picker = $('.au-fonts');
  if (picker) {
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) { io.disconnect(); loadVisibleTiles(); } }, {rootMargin: '0px 0px 120px 0px'});
      io.observe(picker);
    } else loadVisibleTiles();
  }

  // ---- gallery: metal swap, carousel dots, zoom ------------------------------------------------
  const gallery = P('[data-au-gallery]');
  const track = gallery?.querySelector('[data-g-track]');
  const items = gallery ? [...gallery.querySelectorAll('[data-g-item]:not([data-g-empty])')] : [];
  const dots = gallery?.querySelector('[data-g-dots]');
  let shownFinish = null;
  function buildDots() {
    if (!dots) return;
    const visible = [...items, ...(emptyItem ? [emptyItem] : [])].filter(i => !i.hidden);
    dots.innerHTML = visible.length > 1 ? visible.map((_, i) => `<span class="au-pdp-g__dot${i === 0 ? ' is-active' : ''}"></span>`).join('') : '';
  }
  // G1 (owner, 03.11): the gallery shows only the chosen metal (silver plated and 925 share the silver
  // photos). Photos of another metal never show; a finish without its own photo gets a calm empty state.
  const emptyItem = gallery?.querySelector('[data-g-empty]');
  // Layout (desktop): one photo spans both columns, two photos stack large; with an odd count the first photo
  // spans both and the rest sit 2-up. The gallery sticks beside the buy box when it is the shorter column (set once the buy box exists).
  const LEAD_SIZES = '(min-width: 1200px) 59vw, (min-width: 992px) 58vw, 100vw', TILE_SIZES = '(min-width: 1200px) 29vw, (min-width: 992px) 28vw, 100vw';
  let placeGallery = () => {};
  function layoutGallery() {
    if (!track) return;
    const shown = [...items, ...(emptyItem ? [emptyItem] : [])].filter(i => !i.hidden).sort((a, b) => (+a.style.order || 0) - (+b.style.order || 0));
    track.dataset.count = String(shown.length);
    [...items, ...(emptyItem ? [emptyItem] : [])].forEach(i => {
      const lead = (shown.length % 2 === 1 && i === shown[0]) || (shown.length === 2 && shown.includes(i));
      i.classList.toggle('is-lead', lead);
      const img = i.querySelector('img');
      if (img && !img.dataset.zoomed) { const s = lead ? LEAD_SIZES : TILE_SIZES; if (img.sizes !== s) img.sizes = s; }
    });
    placeGallery();
  }
  // A photo's metal: data-metal from the product metafield auriani.media_metal ("Gold Plated", "Rose Plated",
  // "Silver Plated", "925 Sterling Silver", "silver-tone" = both silvers, "neutral"; any other value = never
  // shown alone), else data-finish from the alt text (gold / rose / silver).
  // "unclassified" never shows for a metal, except on a piece where no photo is classified at all.
  const allUnclassified = items.length > 0 && items.every(i => i.dataset.metal === 'unclassified');
  const photoFits = (i, value) => {
    const m = i.dataset.metal;
    if (m && allUnclassified) return 'match';
    if (m) return m === 'neutral' ? 'neutral' : m === value || (m === 'silver-tone' && finishOf(value) === 'silver') ? 'match' : 'no';
    if (!i.dataset.finish) return 'neutral';
    return i.dataset.finish === finishOf(value) ? 'match' : 'no';
  };
  function applyFinish(value, force) {
    if (!gallery || iMetal < 0 || (!force && value === shownFinish)) return;
    shownFinish = value;
    const finish = finishOf(value);
    gallery.dataset.finish = finish;
    const fit = new Map(items.map(i => [i, photoFits(i, value)]));
    const match = items.filter(i => fit.get(i) === 'match');
    let n = 0;
    const place = i => { i.style.order = String(n++); };
    match.forEach(i => { i.hidden = false; place(i); });
    items.filter(i => fit.get(i) === 'neutral').forEach(i => { i.hidden = !match.length; place(i); });
    items.filter(i => fit.get(i) === 'no').forEach(i => { i.hidden = true; i.querySelector('.is-zoomed')?.classList.remove('is-zoomed'); });
    if (emptyItem) {
      emptyItem.hidden = match.length > 0;
      const dot = emptyItem.querySelector('[data-g-empty-dot]');
      if (dot) dot.className = `au-metal__dot au-metal__dot--${finish}`;
      place(emptyItem);
    }
    items.forEach(i => { if (!i.hidden) i.querySelector('img')?.setAttribute('loading', 'eager'); });
    layoutGallery();
    buildDots();
    if (track) track.scrollTo({left: 0, behavior: 'auto'});
  }
  buildDots();
  track?.addEventListener('scroll', () => {
    if (!dots) return;
    const visible = items.filter(i => !i.hidden).sort((a, b) => a.offsetLeft - b.offsetLeft);
    const centre = track.scrollLeft + track.clientWidth / 2;
    let best = 0, dist = Infinity;
    visible.forEach((c, i) => { const d = Math.abs(c.offsetLeft - track.offsetLeft + c.clientWidth / 2 - centre); if (d < dist) { dist = d; best = i; } });
    [...dots.children].forEach((d, i) => d.classList.toggle('is-active', i === best));
  }, {passive: true});
  const zoomDialog = P('[data-zoom-dialog]');
  const inPlaceZoom = () => matchMedia('(hover: hover) and (pointer: fine) and (min-width: 992px)').matches;
  gallery?.addEventListener('click', e => {
    const b = e.target.closest('[data-g-zoom]');
    if (!b) return;
    if (inPlaceZoom()) {
      const on = !b.classList.contains('is-zoomed');
      gallery.querySelectorAll('.is-zoomed').forEach(x => x.classList.remove('is-zoomed'));
      if (on) {
        const img = b.querySelector('img');
        if (img && b.dataset.zoomSrc && !img.dataset.zoomed) { img.srcset = ''; img.sizes = ''; img.src = b.dataset.zoomSrc; img.dataset.zoomed = '1'; }
        const r = b.getBoundingClientRect();
        b.style.setProperty('--zx', `${((e.clientX - r.left) / r.width * 100).toFixed(1)}%`);
        b.style.setProperty('--zy', `${((e.clientY - r.top) / r.height * 100).toFixed(1)}%`);
        b.classList.add('is-zoomed');
      }
      b.setAttribute('aria-pressed', String(on));
    } else if (zoomDialog) {
      const img = b.querySelector('img');
      P('[data-zoom-body]').innerHTML = `<img src="${esc(b.dataset.zoomSrc)}" alt="${esc(img?.alt || '')}">`;
      zoomDialog.showModal();
    }
  });
  gallery?.addEventListener('mousemove', e => {
    const b = e.target.closest('[data-g-zoom].is-zoomed');
    if (!b) return;
    const r = b.getBoundingClientRect();
    b.style.setProperty('--zx', `${((e.clientX - r.left) / r.width * 100).toFixed(1)}%`);
    b.style.setProperty('--zy', `${((e.clientY - r.top) / r.height * 100).toFixed(1)}%`);
  });
  gallery?.addEventListener('mouseleave', () => gallery.querySelectorAll('.is-zoomed').forEach(x => { x.classList.remove('is-zoomed'); x.setAttribute('aria-pressed', 'false'); }));
  zoomDialog?.addEventListener('click', e => { if (e.target === zoomDialog || e.target.closest('[data-zoom-body]')) zoomDialog.close(); });

  // ---- guide drawer: size guide, birthstones, fonts ---------------------------------------------
  const drawer = P('[data-guide-drawer]');
  function openGuide(title, html) {
    if (!drawer) return;
    drawer.querySelector('[data-guide-title]').textContent = title;
    drawer.querySelector('[data-guide-body]').innerHTML = html;
    drawer.showModal();
    drawer.querySelector('[data-close-dialog]')?.focus();
  }
  drawer?.addEventListener('click', e => { if (e.target === drawer) drawer.close(); });
  root.addEventListener('click', e => {
    if (e.target.closest('[data-guide]')) {
      const iLen = chainOptions.find(i => /length/i.test(optionNames[i]));
      const rows = valuesOf(iLen).sort((a, b) => lengthNumber(a) - lengthNumber(b)).map(v => {
        const inch = Math.round(lengthNumber(v) / 2) * 2;
        return `<li><strong>${esc(v)}</strong><span>${esc(LENGTH_FIT[inch] || '')}</span></li>`;
      }).join('');
      openGuide('Chain length guide', `<img src="${esc(sized(lengthGuide, 1000))}"${/CHAIN_LENGTH/i.test(lengthGuide) ? ' class="au-metal-art"' : ''} alt="Necklace lengths on the body, 14 to 24 inches" width="1000" height="1000"><ul class="au-pdp-lengths">${rows}</ul><p class="au-pdp-tip"><strong>Tip:</strong> if you are between two lengths, choose the longer one.</p>`);
    }
    if (e.target.closest('[data-stone-guide]')) openGuide('Birthstone guide', `<img src="${esc(sized(stoneChart.photo.src, 1000))}" alt="${esc(stoneChart.photo.alt)}" width="1000" height="1000"><p class="au-pdp-tip">Choose the month; the stone is set in that month's colour, as on the chart.</p>`);
    if (e.target.closest('[data-font-chart]')) openGuide('Font chart', fontCharts.map(c => `<img src="${esc(sized(c.src, 1200))}" alt="${esc(c.alt)}" loading="lazy">`).join(''));
    const more = e.target.closest('[data-font-toggle]');
    if (more) {
      const open = more.getAttribute('aria-expanded') !== 'true';
      $$('[data-font-more]').forEach(b => { b.hidden = !open && b.getAttribute('aria-checked') !== 'true'; });
      more.setAttribute('aria-expanded', String(open));
      more.textContent = open ? 'Show fewer fonts' : `Show all ${fontField.fonts.length} fonts`;
      if (open) { loadVisibleTiles(); $$('[data-font-sample]').forEach(n => fitSample(n)); }
    }
  });

  // ---- birthstone dropdown ---------------------------------------------------------------------------
  const closeDropdowns = except => $$('[data-dd]').forEach(dd => { if (dd === except) return; dd.querySelector('[data-dd-list]').hidden = true; dd.querySelector('[data-dd-toggle]').setAttribute('aria-expanded', 'false'); });
  root.addEventListener('click', e => {
    const toggle = e.target.closest('[data-dd-toggle]');
    if (toggle) {
      const dd = toggle.closest('[data-dd]'), list = dd.querySelector('[data-dd-list]');
      const open = list.hidden;
      closeDropdowns(dd);
      list.hidden = !open; toggle.setAttribute('aria-expanded', String(open));
      if (open) (list.querySelector('[aria-selected="true"]') || list.querySelector('[role=option]'))?.focus();
    }
  });
  document.addEventListener('click', e => { if (!e.target.closest('[data-dd]')) closeDropdowns(); });
  root.addEventListener('keydown', e => {
    const opt = e.target.closest('[data-dd] [role=option]');
    const dd = e.target.closest('[data-dd]');
    if (!dd) return;
    if (e.key === 'Escape') { closeDropdowns(); dd.querySelector('[data-dd-toggle]').focus(); return; }
    if (!opt || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const all = [...dd.querySelectorAll('[role=option]')], i = all.indexOf(opt);
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? all.length - 1 : Math.min(all.length - 1, Math.max(0, i + (e.key === 'ArrowDown' ? 1 : -1)));
    all[next].focus();
  });

  // ---- installments line (Shop Pay, form | payment_terms) follows the variant ----------------------
  const terms = P('[data-au-terms]');
  terms?.addEventListener('submit', e => e.preventDefault());
  const termsId = terms?.querySelector('[data-au-terms-id]');

  // ---- paint --------------------------------------------------------------------------------------------
  const altPrice = (i, value) => product.variants.find(x => x.options.every((o, j) => (j === Number(i) ? o === value : o === state.options[j])))?.price;
  let barPaint = () => {};
  function paint() {
    const v = variant();
    const price = v ? money(v.price) : '';
    const priceNode = P('[data-price]');
    if (priceNode) priceNode.textContent = price;
    const cmp = v && v.compare_at_price > v.price;
    const was = P('[data-compare]'), off = P('[data-off]');
    if (was) { was.hidden = !cmp; was.textContent = cmp ? money(v.compare_at_price) : ''; }
    if (off) { off.hidden = !cmp; off.textContent = cmp ? `${Math.floor((v.compare_at_price - v.price) * 100 / v.compare_at_price)}% off` : ''; }
    if (termsId && v && termsId.value !== String(v.id)) { termsId.value = String(v.id); termsId.dispatchEvent(new Event('change', {bubbles: true})); }
    if (iMetal >= 0) {
      const name = $('[data-metal-name]'); if (name) name.textContent = finishLabel(state.options[iMetal]);
      $$('[data-metal-price]').forEach(n => { const value = n.dataset.metalPrice; const p = altPrice(iMetal, value) ?? Math.min(...product.variants.filter(x => x.options[iMetal] === value).map(x => x.price)); n.textContent = money(p); });
      applyFinish(state.options[iMetal]);
    }
    $$('[data-option]').forEach(b => b.setAttribute('aria-checked', String(state.options[b.dataset.option] === b.dataset.value)));
    $$('[data-delta]').forEach(node => {
      const [i, value] = node.dataset.delta.split('|');
      const group = valuesOf(Number(i)).map(x => altPrice(i, x)).filter(x => x != null);
      const diff = (altPrice(i, value) ?? 0) - Math.min(...group);
      node.textContent = diff > 0 ? `+ ${money(diff)}` : '';
    });
    chainOptions.forEach(i => { const n = $(`[data-summary="option${i}"]`); if (n) n.textContent = state.options[i]; const s = P(`[data-spec="option${i}"]`); if (s) s.textContent = state.options[i]; });
    const specMetal = P('[data-spec="metal"]'); if (specMetal && iMetal >= 0) specMetal.textContent = finishLabel(state.options[iMetal]);
    rove();
    if (guide?.open) renderGuide();
    const metalClass = `is-${iMetal >= 0 ? finishOf(state.options[iMetal]) : 'gold'}`;
    if (iMetal >= 0 && page.nodeType === 1) page.dataset.auFinish = finishOf(state.options[iMetal]);
    const typed = textFields.map(f => state.values[f.key].trim()).find(Boolean);
    textFields.forEach((f, idx) => {
      const value = state.values[f.key];
      const limit = limitFor(f);
      const left = $(`[data-left="${CSS.escape(f.key)}"]`);
      const len = value.trim().length;
      if (left) { left.textContent = limit ? `${len}/${limit}` : ''; left.classList.toggle('is-over', !!limit && len > limit); left.classList.toggle('is-full', !!limit && len === limit); }
      const input = $(`[data-text="${CSS.escape(f.key)}"]`);
      const over = limit < f.maxLength && len > limit;
      const err = $(`[data-error="${CSS.escape(f.key)}"]`);
      if (over || err.textContent.startsWith('Font ')) err.textContent = over ? textError(f, value) : '';
      if (input) input.setAttribute('aria-invalid', String(!!err.textContent));
      $(`[data-clear="${CSS.escape(f.key)}"]`).hidden = !value;
      const sample = $(`[data-sample="${CSS.escape(f.key)}"]`);
      if (sample) {
        const text = value.trim() || examples[idx] || '';
        sample.parentElement.className = `au-name__sample ${f === colorField ? 'is-plain' : metalClass}${value.trim() ? '' : ' is-example'}`;
        if (f === colorField) { sample.style.fontFamily = ''; sample.removeAttribute('data-wait'); }
        else {
          sample.style.fontFamily = sampleFamily();
          const id = sampleFace();
          const ready = !id || (faceState.has(id) && sample.dataset.face === id && sample.classList.contains('is-ready'));
          if (!ready) {
            sample.dataset.face = id; sample.classList.remove('is-ready');
            loadFace(id).then(() => { if (sample.dataset.face === id) { sample.classList.add('is-ready'); fitSample(sample, 12); } });
          }
        }
        if (sample.textContent !== text) sample.textContent = text;
        fitSample(sample, 12);
      }
    });
    if (fontField) {
      const f = chosenFont();
      const n = $('[data-font-name]'); if (n) n.textContent = f ? `Font ${f.n}` : '';
      $$('[data-font]').forEach(b => { const on = b.dataset.font === String(state.values[fontField.key]); b.setAttribute('aria-checked', String(on)); if (on && b.hasAttribute('data-font-more')) b.hidden = false; });
      $$('[data-font-sample]').forEach(node => { const text = typed ? typed.slice(0, 12) : (examples[0] || 'Abc'); if (node.textContent !== text) { node.textContent = text; fitSample(node); } });
      const row = P('[data-spec-font-row]'); if (row) { row.hidden = !f; P('[data-spec="font"]').textContent = f ? `Font ${f.n}` : ''; }
    } else if (fixedStyle) {
      const row = P('[data-spec-font-row]'); if (row) { row.hidden = false; P('[data-spec="font"]').textContent = 'As shown in the photos'; }
    }
    selectFields.forEach((f, i) => {
      const s = $(`[data-summary="select${i}"]`); if (s) s.textContent = state.values[f.key];
      const dd = $(`[data-step="select${i}"] [data-dd]`);
      if (dd) { dd.querySelector('[data-dd-label]').textContent = state.values[f.key] || 'Select birthstone'; dd.querySelector('[data-dd-img]').innerHTML = state.values[f.key] ? stoneImage(state.values[f.key]) : ''; }
    });
    $$('[data-select]').forEach(b => { const on = state.values[b.dataset.select] === b.dataset.value; b.setAttribute(b.getAttribute('role') === 'option' ? 'aria-selected' : 'aria-checked', String(on)); });
    const add = $('[data-add]');
    const outOfMarket = !!v && !product.variants.some(x => x.available);
    const label = !v ? 'Unavailable' : v.available ? `${editKey ? 'Update bag' : 'Add to bag'} · ${price}` : outOfMarket ? 'Not deliverable to your country' : 'Sold out';
    if (add) {
      $('[data-add-label]').textContent = label;
      if (add.getAttribute('aria-busy') !== 'true') add.disabled = !v || !v.available;
      $('[data-ship-country]').hidden = !outOfMarket;
    }
    barPaint(v, price, cmp, label);
    const url = new URL(location.href);
    if (v) url.searchParams.set('variant', v.id);
    fields.forEach(f => { const value = state.values[f.key]; if (value) url.searchParams.set(`p_${f.key}`, value); else url.searchParams.delete(`p_${f.key}`); });
    history.replaceState(history.state, '', url);
  }

  // ---- interactions ---------------------------------------------------------------------------------------
  root.addEventListener('click', e => {
    const t = e.target;
    const opt = t.closest('[data-option]');
    if (opt) { state.options[opt.dataset.option] = opt.dataset.value; if (Number(opt.dataset.option) === iMetal) remember(); paint(); }
    const font = t.closest('[data-font]');
    if (font) { state.values[fontField.key] = font.dataset.font; $(`[data-error="${CSS.escape(fontField.key)}"]`).textContent = ''; paint(); }
    const sel = t.closest('[data-select]');
    if (sel) {
      state.values[sel.dataset.select] = sel.dataset.value; $(`[data-error="${CSS.escape(sel.dataset.select)}"]`).textContent = ''; paint();
      const dd = sel.closest('[data-dd]'); if (dd) { closeDropdowns(); dd.querySelector('[data-dd-toggle]').focus(); }
    }
    const clear = t.closest('[data-clear]');
    if (clear) { const k = clear.dataset.clear; state.values[k] = ''; const input = $(`[data-text="${CSS.escape(k)}"]`); input.value = ''; input.focus(); paint(); }
  });
  $$('[data-text]').forEach(input => {
    const f = textFields.find(x => x.key === input.dataset.text);
    input.value = state.values[f.key];
    input.addEventListener('input', () => {
      state.values[f.key] = input.value;
      $(`[data-error="${CSS.escape(f.key)}"]`).textContent = '';
      paint(); remember();
    });
    input.addEventListener('blur', () => { const err = state.values[f.key] && textError(f, state.values[f.key]); $(`[data-error="${CSS.escape(f.key)}"]`).textContent = err || ''; input.setAttribute('aria-invalid', String(!!err)); });
  });

  // ---- delivery estimate (P1): one calculator, window.AurianiETA in auriani-v7.js (the same code as v7p:
  // business days, weekends skipped); the days come from the theme settings, not the product metafield.
  const days = config.days || {};
  const make = ui.delivery?.make && days.make, ship = ui.delivery?.ship && days.ship;
  const etaCalc = window.AurianiETA;
  const eta = P('[data-eta]');
  if (make && ship && etaCalc) {
    const [from, to] = etaCalc.range(make, ship);
    if (eta) eta.textContent = `Ordered today, a US order is estimated to arrive ${etaCalc.format(from, 'long')} – ${etaCalc.format(to, 'long')} with standard shipping.`;
    const line = $('[data-au-eta-line]');
    if (line) { line.querySelector('[data-au-eta-text]').textContent = `Order today · Estimated US delivery ${etaCalc.format(from)} – ${etaCalc.format(to)}`; line.hidden = false; }
  }

  // ---- M2: every radiogroup is one tab stop; arrow keys move the choice (ARIA radio pattern) ------------------
  function rove() {
    $$('[role=radiogroup]').forEach(group => {
      const radios = [...group.querySelectorAll('[role=radio]')].filter(r => !r.hidden && !r.disabled);
      const on = radios.find(r => r.getAttribute('aria-checked') === 'true') || radios[0];
      radios.forEach(r => { r.tabIndex = r === on ? 0 : -1; });
    });
  }
  root.addEventListener('keydown', e => {
    const radio = e.target.closest('[role=radio]');
    if (!radio || !['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const radios = [...radio.closest('[role=radiogroup]').querySelectorAll('[role=radio]')].filter(r => !r.hidden && !r.disabled);
    const i = radios.indexOf(radio);
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? radios.length - 1 : (i + (/Right|Down/.test(e.key) ? 1 : -1) + radios.length) % radios.length;
    e.preventDefault();
    radios[next].focus();
    radios[next].click();
  });

  // ---- M3: the metal guide, one card per finish this piece is offered in -----------------------------------------
  const guide = page.querySelector(`#AuPdpMaterial-${CSS.escape(sid)}`);
  const CARE = ['Take it off before swimming, showering and exercise.', 'Put on perfume, lotion and hairspray before your jewelry.', 'Store it dry, on its own, in a pouch or box.', 'Wipe it with a soft, dry cloth after wearing.'];
  function renderGuide() {
    const body = guide?.querySelector('[data-metal-guide]');
    if (!body || iMetal < 0) return;
    const metals = valuesOf(iMetal).sort((a, b) => /925/.test(a) - /925/.test(b));
    body.innerHTML = `<ul class="au-mg" role="list">${metals.map(v => {
      const on = state.options[iMetal] === v;
      const p = altPrice(iMetal, v) ?? Math.min(...product.variants.filter(x => x.options[iMetal] === v).map(x => x.price));
      return `<li class="au-mg__card${on ? ' is-current' : ''}"><span class="au-metal__dot au-metal__dot--${finishOf(v)}" aria-hidden="true">${/925/.test(v) ? '925' : ''}</span>
        <div class="au-mg__text"><h3 class="au-mg__name">${esc(finishLabel(v))}</h3><p>${esc(finishLine(v))}</p></div>
        <div class="au-mg__end"><span class="au-mg__price">${money(p)}</span>${on ? '<span class="au-mg__on">Selected</span>' : `<button type="button" class="au-btn au-btn--sm au-btn--secondary" data-mg-select="${esc(v)}" aria-label="Select ${esc(finishLabel(v))}">Select</button>`}</div></li>`;
    }).join('')}</ul>
    ${nickelFree ? '<p class="au-mg__note"><strong>Every finish is hypoallergenic and nickel-free.</strong></p>' : ''}
    <h3 class="au-mg__h">Caring for your piece</h3>
    <ul class="au-mg__care">${CARE.map(c => `<li>${esc(c)}</li>`).join('')}</ul>
    <p class="au-mg__small">The photos show each finish. Choose a finish to see its photos and its price.</p>`;
  }
  root.addEventListener('click', e => { if (e.target.closest('.au-opt__help')) renderGuide(); });
  guide?.addEventListener('click', e => {
    const b = e.target.closest('[data-mg-select]');
    if (!b) return;
    state.options[iMetal] = b.dataset.mgSelect; remember(); paint();
    guide.close();
  });

  // ---- M4: "Size & materials" from the piece's own facts ----------------------------------------------------------
  const specBody = P('[data-spec-body]');
  if (specBody) {
    const inch = (mm, top = mm) => (mm / 25.4).toFixed(top / 25.4 >= 1 ? 1 : 2);
    const sizeRows = [];
    // sizes exactly as the piece's facts state them in mm; a figure without a unit is left out, never guessed
    descItems.filter(s => /\d\s*mm/i.test(s) && !/^materials?\s*:/i.test(s)).forEach(s => {
      const lead = (s.match(/^(.*?)\s*\b(?:width|height|diameter|length|size|thickness)\b/i) || [])[1]?.replace(/[:,]\s*$/, '').trim();
      const parts = [...s.matchAll(/(?:\b(width|height|diameter|length|thickness|size)\b\s*:?\s*)?(\d+(?:\.\d+)?)(?:\s*(?:-|–|to)\s*(\d+(?:\.\d+)?))?(?:\s*[*×x]\s*(\d+(?:\.\d+)?))?\s*mm/gi)].map(m => {
        const [, kw, a1, a2, b1] = m, A = parseFloat(a1), A2 = a2 ? parseFloat(a2) : null, B = b1 ? parseFloat(b1) : null;
        const name = kw && !/size/i.test(kw) ? `${kw[0].toUpperCase()}${kw.slice(1).toLowerCase()} ` : '';
        if (B) return `${name}${A} × ${B} mm (${inch(A, Math.max(A, B))} × ${inch(B, Math.max(A, B))} in)`;
        return `${name}${A2 ? `${A}–${A2}` : A} mm (${A2 ? `${inch(A, A2)}–${inch(A2)}` : inch(A)} in)`;
      });
      if (parts.length) sizeRows.push([lead ? `${lead[0].toUpperCase()}${lead.slice(1)}` : 'Size', parts.join(' · ')]);
    });
    const rows = [...sizeRows];
    if (iMetal >= 0 && !singleDefault) rows.push(['Finishes', valuesOf(iMetal).sort((a, b) => /925/.test(a) - /925/.test(b)).map(finishLabel).join(' · ')]);
    if (plating14k) rows.push(['Plating', roseIn14k ? '14k gold or 14k rose gold, on the plated finishes' : '14k gold, on the plated finishes']);
    chainOptions.forEach(i => {
      if (singleDefault) return;
      const isLength = /length/i.test(optionNames[i]);
      const vals = valuesOf(i);
      if (isLength) {
        const sorted = [...vals].sort((a, b) => lengthNumber(a) - lengthNumber(b));
        rows.push([/chain/i.test(optionNames[i]) || !/bracelet|anklet/i.test(product.type + ' ' + product.title) ? 'Chain lengths' : optionNames[i], sorted.join(' · ')]);
      } else rows.push([/chain/i.test(optionNames[i]) ? 'Chain styles' : optionNames[i], vals.map(v => v.replace(/\s*chain$/i, '')).join(' · ')]);
    });
    if (nickelFree) rows.push(['Skin', 'Hypoallergenic and nickel-free, every finish']);
    if (fontField) rows.push(['Lettering', `${fontField.fonts.length} lettering styles`]);
    if (rows.length) specBody.innerHTML = rows.map(([k, v]) => `<tr><th scope="row">${esc(k)}</th><td>${esc(v)}</td></tr>`).join('');
  }

  // ---- wish list (as v7p) -----------------------------------------------------------------------------------
  P('[data-save]')?.addEventListener('click', () => {
    if (!window.AurianiSaved) return;
    const v = variant();
    const item = window.AurianiSaved.save({id: v.id, handle: product.handle, title: product.title, url: location.pathname + location.search, image: photos[0]?.src, price: v.price, properties: properties()});
    P('[data-save]').setAttribute('aria-pressed', String(Boolean(item)));
  });

  // ---- add to bag: supplier keys exactly (as v7p) ---------------------------------------------------------
  function properties() {
    const out = {};
    textFields.forEach(f => { const v = state.values[f.key].trim(); if (v) out[f.key] = v; });
    selectFields.forEach(f => { if (state.values[f.key]) out[f.key] = state.values[f.key]; });
    if (fontField && chosenFont()) { out[fontField.key] = String(chosenFont().n); out._jwl_font = String(chosenFont().id); }
    if (ui.orderNote) out._auriani_note = '1';
    return out;
  }
  const cartUrl = () => {
    const url = new URL(config.cartUrl || '/cart', location.origin);
    const preview = params.get('preview_theme_id'); if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.pathname + url.search;
  };
  $('[data-add]')?.addEventListener('click', async () => {
    const addError = $('[data-add-error]');
    addError.textContent = '';
    const problems = [];
    textFields.forEach(f => { const err = textError(f, state.values[f.key]); $(`[data-error="${CSS.escape(f.key)}"]`).textContent = err || ''; $(`[data-text="${CSS.escape(f.key)}"]`)?.setAttribute('aria-invalid', String(!!err)); if (err) problems.push($(`[data-text="${CSS.escape(f.key)}"]`)); });
    if (fontField?.required && !chosenFont()) { $(`[data-error="${CSS.escape(fontField.key)}"]`).textContent = 'Please choose a font.'; problems.push($('[data-font]')); }
    selectFields.forEach((f, i) => { if (f.required && !state.values[f.key]) { $(`[data-error="${CSS.escape(f.key)}"]`).textContent = 'This field is required.'; problems.push($(`[data-step="select${i}"] button`)); } });
    if (problems.length) {
      const first = problems.find(Boolean);
      first?.scrollIntoView({block: 'center', behavior: reduced() ? 'auto' : 'smooth'});
      first?.focus({preventScroll: true});
      return;
    }
    if (!window.AurianiCart) { addError.textContent = 'The bag is not available. Please reload the page.'; return; }
    const button = $('[data-add]');
    button.disabled = true; button.setAttribute('aria-busy', 'true');
    let added = false;
    try {
      const line = editKey && (await window.AurianiCart.read()).items.find(i => i.key === editKey);
      if (line) {
        const props = properties();
        Object.keys(line.properties || {}).forEach(k => { if (!(k in props) && !k.startsWith('_auriani_cart')) props[k] = ''; });
        await window.AurianiCart.replace(line, {id: variant().id, quantity: line.quantity, properties: props});
        editKey = null;
        const url = new URL(location.href);
        url.searchParams.delete('edit'); url.searchParams.delete('edit_loaded');
        history.replaceState(null, '', url);
      } else {
        await window.AurianiCart.add({items: [{id: variant().id, quantity: 1, properties: properties()}]});
        added = true;
      }
    } catch (_) {
      addError.textContent = 'This could not be added to your bag. Please try again.';
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = !variant()?.available;
      paint();
    }
    // Desktop: the cart drawer opens (cart.js). Mobile: straight to the bag page, as onecklace.
    if (added && mobile()) location.assign(cartUrl());
  });

  // ---- sticky purchase bar (v7.4, owner 03.11, desktop and mobile): one bar, shown while the main Add to bag is
  // out of view. Its button is a shortcut to the main button, so the cart line, the checks and the ?edit= flow are
  // the same code path: "Customize" while a required choice is missing or invalid (the main button then scrolls
  // to that field and shows its hint), else "Add to bag · price".
  const bar = P('[data-bar]');
  const mainAdd = $('[data-add]');
  if (bar && mainAdd) {
    const save = P('[data-save]');
    const heart = !!(save && window.AurianiSaved);
    bar.innerHTML = `<div class="au-pdp-bar__inner">
      <span class="au-pdp-bar__thumb" data-bar-thumb aria-hidden="true"></span>
      <div class="au-pdp-bar__text"><p class="au-pdp-bar__title">${esc(product.title)}</p><p class="au-pdp-bar__choices" data-bar-choices></p></div>
      <p class="au-pdp-bar__price"><span class="au-visually-hidden">Price: </span><span data-bar-price aria-live="polite"></span><s data-bar-compare hidden></s></p>
      <button type="button" class="au-btn au-pdp-bar__add" data-bar-add></button>
      ${heart ? `<button type="button" class="au-icon-btn au-pdp-bar__save" data-bar-save aria-pressed="${save.getAttribute('aria-pressed') || 'false'}" aria-label="Save this piece">${save.innerHTML}</button>` : ''}
    </div>`;
    const B = s => bar.querySelector(s);
    // the same checks as the main button, without side effects
    const incomplete = () => textFields.some(f => textError(f, state.values[f.key])) || (fontField?.required && !chosenFont()) || selectFields.some(f => f.required && !state.values[f.key]);
    const shortLength = v => { const m = String(v).match(/^(\d+(?:\.\d+)?)\s*["”″]/); return m ? `${m[1]}″` : v; };
    let thumbKey = '';
    barPaint = (v, price, cmp, label) => {
      const priceNode = B('[data-bar-price]'); if (priceNode.textContent !== price) priceNode.textContent = price;
      const bc = B('[data-bar-compare]'); bc.hidden = !cmp; bc.textContent = cmp ? money(v.compare_at_price) : '';
      const ba = B('[data-bar-add]');
      const ok = !!v && v.available;
      const todo = ok && incomplete();
      ba.disabled = !ok;
      ba.dataset.mode = todo ? 'customize' : ok ? 'add' : 'off';
      ba.textContent = !ok ? label : todo ? 'Customize' : `${editKey ? 'Update bag' : 'Add to bag'} · ${price}`;
      const parts = [];
      if (iMetal >= 0 && !singleDefault) parts.push(finishLabel(state.options[iMetal]));
      if (!singleDefault) chainOptions.forEach(i => { if (/length/i.test(optionNames[i])) parts.push(shortLength(state.options[i])); });
      if (fontField && chosenFont()) parts.push(`Font ${chosenFont().n}`);
      textFields.forEach(f => { const t = state.values[f.key].trim(); if (t) parts.push(`“${t}”`); });
      selectFields.forEach(f => { if (state.values[f.key]) parts.push(state.values[f.key]); });
      B('[data-bar-choices]').textContent = parts.join(' · ');
      // G1: the photo now shown for the chosen finish, or the calm "coming soon" dot
      const shown = [...(gallery?.querySelectorAll('[data-g-item], [data-g-empty]') || [])].filter(i => !i.hidden).sort((a, b) => (+a.style.order || 0) - (+b.style.order || 0))[0];
      const img = shown && !shown.hasAttribute('data-g-empty') && shown.querySelector('img');
      const src = img ? (img.currentSrc || img.src) : '';
      const key = src || `soon:${iMetal >= 0 ? finishOf(state.options[iMetal]) : ''}`;
      if (key !== thumbKey) {
        thumbKey = key;
        const thumb = B('[data-bar-thumb]');
        thumb.innerHTML = src ? `<img src="${esc(src)}" alt="" width="44" height="44" decoding="async">` : (iMetal >= 0 ? `<span class="au-metal__dot au-metal__dot--${finishOf(state.options[iMetal])}"></span>` : '');
      }
    };
    const priceRow = P('[data-price-row]');
    const footer = document.querySelector('.au-footer') || document.querySelector('footer');
    const headerH = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--au-header-h')) || 60;
    const bannerUp = () => { const b = document.getElementById('shopify-pc__banner'); return !!b && b.offsetHeight > 0 && getComputedStyle(b).visibility !== 'hidden'; };
    let frame = 0, shownNow = null;
    const place = () => {
      frame = 0;
      const ar = mainAdd.getBoundingClientRect(), pr = priceRow?.getBoundingClientRect();
      const out = ar.bottom < headerH() || (ar.top > innerHeight && !!pr && pr.bottom < headerH());
      const nearFooter = !!footer && footer.getBoundingClientRect().top < innerHeight;
      const show = out && !nearFooter && !bannerUp();
      bar.hidden = false;
      if (show === shownNow) return;
      shownNow = show;
      bar.classList.toggle('is-shown', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.inert = !show;
      bar.querySelectorAll('button').forEach(b => { b.tabIndex = show ? 0 : -1; });
      document.documentElement.style.setProperty('--au-float-bottom', show ? `${bar.offsetHeight + 16}px` : '');
    };
    addEventListener('scroll', () => { frame ||= requestAnimationFrame(place); }, {passive: true});
    addEventListener('resize', () => { frame ||= requestAnimationFrame(place); });
    setInterval(() => { if (shownNow && bannerUp()) place(); }, 1000);
    place();
    B('[data-bar-add]').addEventListener('click', () => mainAdd.click());
    const syncHeart = () => { const h = B('[data-bar-save]'); if (h && save) h.setAttribute('aria-pressed', save.getAttribute('aria-pressed') || 'false'); };
    B('[data-bar-save]')?.addEventListener('click', () => { save.click(); syncHeart(); });
    document.addEventListener('auriani:saved-changed', syncHeart);
  }

  // ---- sticky buy box: pinned under the header; a box taller than the screen pins by its bottom ----------
  const buy = P('[data-au-buy]');
  const inner = buy?.querySelector('.au-pdp-buy__inner');
  if (inner) {
    const fit = () => {
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--au-header-h')) || 60;
      const top = header + 24, h = inner.offsetHeight;
      inner.style.setProperty('--au-pdp-top', `${h + top + 24 > innerHeight ? Math.min(top, innerHeight - h - 24) : top}px`);
    };
    if ('ResizeObserver' in window) new ResizeObserver(fit).observe(inner);
    addEventListener('resize', fit);
    fit();
    // The gallery sticks beside a taller buy box (desktop): pinned under the header when it fits the screen,
    // otherwise by its bottom edge, so no empty column shows while the options scroll past.
    placeGallery = () => {
      if (!gallery) return;
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--au-header-h')) || 60;
      const gh = gallery.offsetHeight;
      const on = matchMedia('(min-width: 992px)').matches && gh > 0 && gh + 1 < inner.offsetHeight;
      gallery.classList.toggle('is-sticky', on);
      gallery.style.setProperty('--au-pdp-gtop', `${gh + header <= innerHeight ? header : innerHeight - gh}px`);
    };
    if ('ResizeObserver' in window) { const ro = new ResizeObserver(() => placeGallery()); ro.observe(inner); if (gallery) ro.observe(gallery); }
    addEventListener('resize', () => placeGallery());
    placeGallery();
  }

  // ---- guide CTA, contact form state -------------------------------------------------------------------------
  P('[data-cta-start]')?.addEventListener('click', () => {
    const target = $('[data-text]') || $('[data-option]') || $('[data-add]');
    target?.scrollIntoView({block: 'center', behavior: reduced() ? 'auto' : 'smooth'});
    target?.focus({preventScroll: true});
  });
  if (P('[data-ask-posted]') || /contact_posted=true/.test(location.search)) P('[data-ask-dialog]')?.showModal();

  // ---- start ---------------------------------------------------------------------------------------------------
  if (iMetal >= 0) applyFinish(state.options[iMetal], true);
  paint();
  rove();
  window.AurianiPDP = {state, properties, variant, version: 'v8b'};
})();